'use client';

import { useEffect, useRef, useState } from 'react';

import throttle from 'lodash.throttle';

import { SegmentedButtons } from '@gds/SegmentedButtons/SegmentedButtons';

import type { ContentsLink } from './ContentsLinks.types';
import { getActiveSectionId } from './Utils/GetActiveSectionId';
import { getTopInset } from './Utils/GetTopInset';

import styles from './ContentsLinksBar.module.css';

type ContentsLinksBarProps = {
  links: ContentsLink[];
};

export const ContentsLinksBar = ({ links }: ContentsLinksBarProps) => {
  const [activeSection, setActiveSection] = useState<string | undefined>(links[0]?.id);
  const [topInset, setTopInset] = useState(0);
  const ref = useRef<HTMLElement>(null);
  const segmentedButtonsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const ids = links.map(({ id }) => id);

    const handleScroll = throttle(() => {
      if (!ref.current) return;
      setTopInset(getTopInset(ref.current));
      const id = getActiveSectionId(ids, ref.current.getBoundingClientRect().bottom + 1);
      if (id) setActiveSection(id);
    }, 100);

    handleScroll();
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
      handleScroll.cancel();
    };
  }, [links]);

  const prevActiveSection = useRef(activeSection);
  useEffect(() => {
    if (prevActiveSection.current === activeSection) return;
    prevActiveSection.current = activeSection;
    if (!segmentedButtonsRef.current || !activeSection) return;
    const activeBtn =
      segmentedButtonsRef.current.querySelector<HTMLElement>('[aria-checked="true"]');
    if (!activeBtn) return;

    const scroller = segmentedButtonsRef.current.parentElement;
    if (!scroller) return;
    const btnRect = activeBtn.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    if (btnRect.left < scrollerRect.left || btnRect.right > scrollerRect.right) {
      scroller.scrollLeft +=
        btnRect.left - scrollerRect.left - (scrollerRect.width - btnRect.width) / 2;
    }
  }, [activeSection]);

  const handleSectionChange = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (!section || !ref.current) return;
    const isSticky = getComputedStyle(ref.current).position === 'sticky';
    const scrollOffset = getTopInset(section) + (isSticky ? ref.current.offsetHeight : 0);

    window.scrollTo({
      top: window.scrollY + section.getBoundingClientRect().top - scrollOffset,
      behavior: 'smooth',
    });
  };
  return (
    <section className={styles.wrapper} ref={ref} style={{ top: topInset }}>
      <div className={styles.scroller}>
        <div ref={segmentedButtonsRef}>
          <SegmentedButtons
            className={styles.tabs}
            currentValue={activeSection}
            buttons={links.map(({ id, label }) => ({ label, value: id }))}
            onChange={handleSectionChange}
          />
        </div>
      </div>
    </section>
  );
};
